import React from 'react';
import { ActivityIndicator, View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import Text from '../../components/text/Text';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import PressableScale from './PressableScale';

/**
 * SecondaryButton: supporting actions ("Run brief", "Route map", "Add photos",
 * "Shift pass", "Report a wrong value").
 * Props:
 * - label (string), onPress, onLongPress, disabled, loading
 * - icon: Feather icon name before the label
 * - variant: 'outline' (bordered pill) | 'tonal' (filled chip colour, no border)
 * - shape: 'pill' | 'rounded' (12 px corners, e.g. Shift pass on Home)
 * - accessibilityLabel, accessibilityHint, style
 */
const SecondaryButton = ({
	label,
	onPress,
	onLongPress,
	disabled = false,
	loading = false,
	icon,
	variant = 'outline',
	shape = 'pill',
	accessibilityLabel,
	accessibilityHint,
	style,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui } } = useTheme();
	const isInactive = disabled || loading;
	const contentColor = disabled ? ui.colors.disabledText : ui.colors.onChip;

	return (
		<PressableScale
			onPress={isInactive ? undefined : onPress}
			onLongPress={isInactive ? undefined : onLongPress}
			disabled={isInactive}
			accessibilityRole="button"
			accessibilityLabel={accessibilityLabel || label}
			accessibilityHint={accessibilityHint}
			accessibilityState={{ disabled, busy: loading }}
			style={({ pressed }) => [
				styles.button,
				variant === 'tonal' && styles.tonal,
				shape === 'rounded' && styles.rounded,
				disabled && styles.disabled,
				pressed && styles.pressed,
				style,
			]}
		>
			{
				loading ? (
					<ActivityIndicator color={contentColor} />
				) : (
					<View style={styles.content}>
						{Boolean(icon) && <Feather name={icon} size={18} color={contentColor} />}
						<Text style={[styles.label, disabled && styles.labelDisabled]} numberOfLines={2}>{label}</Text>
					</View>
				)
			}
		</PressableScale>
	);
};

const createStyles = ({ ui: { colors, radius, touch, type, space } }) => ({
	button: {
		minHeight: touch.min,
		borderRadius: radius.pill,
		borderWidth: 1,
		borderColor: colors.borderStrong,
		backgroundColor: colors.card,
		alignItems: 'center',
		justifyContent: 'center',
		paddingHorizontal: space.lg,
		paddingVertical: space.xs,
	},
	tonal: {
		borderColor: colors.chip,
		backgroundColor: colors.chip,
	},
	rounded: {
		borderRadius: radius.card,
	},
	// The outline keeps a disabled button visible where disabledBg matches the card (Light);
	// the opacity fades it so it never reads as an available button.
	disabled: {
		borderColor: colors.borderStrong,
		backgroundColor: colors.disabledBg,
		opacity: 0.5,
	},
	pressed: {
		opacity: 0.8,
	},
	content: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		gap: space.sm,
	},
	label: {
		...type.bodySmallStrong,
		color: colors.onChip,
		textAlign: 'center',
	},
	labelDisabled: {
		color: colors.disabledText,
	},
});

export default SecondaryButton;
